import { useState, useCallback } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ModeButtons from "../../../pages/Home/components/ModeButtons";
import ChatSidebar from "../../analyze/components/ChatSidebar";
import MessagesContainer from "../components/MessageContainer";
import ChatInput from "../components/ChatInput";
import useChatGptPage from "../hooks/useChatGptPage";
import useScrollFade from "../../../ui/hooks/useScrollFade";
import {
  DESKTOP_MODE_STRIP_PADDING_X,
  mobileNavMediaQuery,
  SIDEBAR_COLLAPSED_WIDTH_PX,
} from "../../../layout/layoutMetrics";
import { useSidebar } from "../../../layout/SidebarContext";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../../pages/Home/constants/homeGlass";

export default function ChatGptPage() {
  const {
    messages,
    prompt,
    setPrompt,
    mediaType,
    setMediaType,
    copiedMessageId,
    setCopiedMessageId,
    messagesEndRef,
    handleDownload,
    revealGeneratedVideo,
    handleSend,
    conversations,
    currentChatSessionId,
    loadingConversations,
    deletingConversationIds,
    renamingConversationIds,
    isGenerating,
    showVideoNotificationPrompt,
    dismissVideoNotificationPrompt,
    confirmVideoNotification,
    showAlert,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleRenameConversation,
    isScriptOnlyPage,
  } = useChatGptPage();

  const isMobileNav = useMediaQuery(mobileNavMediaQuery);
  const { isSidebarOpen } = useSidebar();
  const [chatsDrawerOpen, setChatsDrawerOpen] = useState(false);
  const messageScroll = useScrollFade();

  const handleSelectConversationMobile = useCallback(
    async (session) => {
      await handleSelectConversation(session);
      setChatsDrawerOpen(false);
    },
    [handleSelectConversation],
  );

  const handleNewChatMobile = useCallback(() => {
    handleNewChat();
    setChatsDrawerOpen(false);
  }, [handleNewChat]);

  const chatSidebarSharedProps = {
    conversations,
    currentChatSessionId,
    loadingConversations,
    deletingConversationIds,
    renamingConversationIds,
    isGenerating,
    showAlert,
    onDeleteConversation: handleDeleteConversation,
    onRenameConversation: handleRenameConversation,
  };

  const renderChatsSection = (forMobileDrawer) => (
    <ChatSidebar
      {...chatSidebarSharedProps}
      onNewChat={forMobileDrawer ? handleNewChatMobile : handleNewChat}
      onSelectConversation={
        forMobileDrawer
          ? handleSelectConversationMobile
          : handleSelectConversation
      }
    />
  );

  return (
    <Box
      sx={{
        height: "100%",
        flex: 1,
        minHeight: 0,
        position: "relative",
        zIndex: 2,
        display: "flex",
        overflow: "hidden",
        color: "white",
        fontFamily: "Inter, sans-serif",
        background: "transparent",
        [`@media ${mobileNavMediaQuery}`]: {
          flex: 1,
          height: "100%",
          minHeight: 0,
          width: "100%",
          maxWidth: "100%",
        },
      }}
    >
      {!isMobileNav && isSidebarOpen && (
        <Box
          sx={{
            boxSizing: "border-box",
            width: 200,
            minWidth: 200,
            maxWidth: 200,
            flexShrink: 0,
            p: 2.7,
            pt: "calc(var(--header-project-panel-bottom, 256px) - 20px)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            background: "transparent",
          }}
        >
          {renderChatsSection(false)}
        </Box>
      )}

      {isMobileNav && (
        <Drawer
          anchor="left"
          open={chatsDrawerOpen}
          onClose={() => setChatsDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            zIndex: 1250,
            "& .MuiDrawer-paper": {
              width: "min(300px, 88vw)",
              maxWidth: "100vw",
              boxSizing: "border-box",
              height: "var(--app-vvh, 100dvh)",
              maxHeight: "var(--app-vvh, 100dvh)",
              background: homeGlassHero.rest,
              backdropFilter: HOME_GLASS_BACKDROP,
              WebkitBackdropFilter: HOME_GLASS_BACKDROP,
              borderRight: HOME_GLASS_BORDER,
              pt: "max(12px, env(safe-area-inset-top, 0px))",
              px: 2,
              pb: "max(16px, env(safe-area-inset-bottom, 0px))",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {renderChatsSection(true)}
          </Box>
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          ml: !isMobileNav && !isSidebarOpen ? `${SIDEBAR_COLLAPSED_WIDTH_PX}px` : 0,
        }}
      >
        <Box
          sx={{
            mt: 0.5,
            px: DESKTOP_MODE_STRIP_PADDING_X,
            flexShrink: 0,
            [`@media ${mobileNavMediaQuery}`]: { px: 1.5 },
          }}
        >
          <ModeButtons />
        </Box>

        <Box
          sx={{
            flex: 1,
            pr: 4,
            pl: 3,
            mt: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            [`@media ${mobileNavMediaQuery}`]: {
              pr: 1.5,
              pl: 1.5,
              mt: 1,
            },
          }}
        >
            <Box
              sx={{
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                borderRadius: "16px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                flexShrink: 0,
                [`@media ${mobileNavMediaQuery}`]: {
                  height: 52,
                  gap: 1,
                  px: 1.5,
                  minWidth: 0,
                },
              }}
            >
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 500,
                [`@media ${mobileNavMediaQuery}`]: {
                  fontSize: "1rem",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              Generate
            </Typography>
            {isMobileNav && (
              <Tooltip title="Your chats">
                <IconButton
                  aria-label="Open your chats"
                  size="small"
                  onClick={() => setChatsDrawerOpen(true)}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.06)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                  }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              mt: 1,
              borderRadius: "20px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.05)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              mb: 1,
            }}
          >
            <Box
              ref={messageScroll.scrollRef}
              onScroll={messageScroll.onScroll}
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                scrollbarWidth: "thin",
                ...messageScroll.scrollFadeSx,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: "3px",
                },
              }}
            >
              <MessagesContainer
                messages={messages}
                copiedMessageId={copiedMessageId}
                setCopiedMessageId={setCopiedMessageId}
                handleDownload={handleDownload}
                onViewGeneratedVideo={revealGeneratedVideo}
                messagesEndRef={messagesEndRef}
              />
            </Box>

            <Box
              sx={{
                pt: 1,
                px: 1,
                background: "transparent",
                flexShrink: 0,
                [`@media ${mobileNavMediaQuery}`]: {
                  px: 0.5,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                },
              }}
            >
              <ChatInput
                prompt={prompt}
                setPrompt={setPrompt}
                handleSend={handleSend}
                mediaType={mediaType}
                setMediaType={setMediaType}
                disabled={isGenerating}
                scriptOnly={isScriptOnlyPage}
                showVideoNotificationPrompt={showVideoNotificationPrompt}
                onDismissVideoNotificationPrompt={dismissVideoNotificationPrompt}
                onNotifyVideoGenerated={confirmVideoNotification}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
